import { WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants'
import {
  ADVISOR_TEAM_UPGRADE_PRODUCT_ID,
  FREE_WIL_MESSAGE_LIMIT,
  WIL_AI_UPGRADE_PRODUCT_ID,
} from '@src/components/sco/Advisors/model/access'

export type UpgradePaywallIntent = 'explore' | 'marketplace' | 'team' | 'onPro' | 'onTeam'
export type UpgradeMembership = 'free' | 'pro' | 'team'
export type UpgradeCompareValue = true | false | string

export const UPGRADE_COMPARE_GROUPS: {
  label: string
  rows: {
    feature: string
    free: UpgradeCompareValue
    pro: UpgradeCompareValue
    team: UpgradeCompareValue
  }[]
}[] = [
  {
    label: 'As a Founder',
    rows: [
      {
        feature: `Chat with ${WIL_DISPLAY_NAME}`,
        free: `${FREE_WIL_MESSAGE_LIMIT} messages`,
        pro: 'Unlimited',
        team: 'Unlimited',
      },
      { feature: 'Browse the directory', free: true, pro: true, team: true },
      { feature: 'Open a board seat', free: true, pro: true, team: true },
      { feature: 'Accept applications on your seat', free: true, pro: true, team: true },
      { feature: 'Message marketplace advisors', free: false, pro: true, team: true },
      { feature: 'Invite to your board', free: false, pro: true, team: true },
      { feature: 'Inbox with marketplace advisors', free: false, pro: true, team: true },
      { feature: 'Send & sign FAST agreements', free: false, pro: true, team: true },
      { feature: 'Book a paid session', free: 'Your card', pro: 'Your card', team: 'Your card' },
      { feature: 'DM Ed, Ryan, Steve, and Wil', free: false, pro: false, team: true },
      { feature: 'Team office hours (Events)', free: false, pro: false, team: true },
      { feature: 'Premium events & advisor-led sprints', free: false, pro: false, team: true },
    ],
  },
  {
    label: 'As an Advisor',
    rows: [
      { feature: 'Submit an advisor application', free: true, pro: true, team: true },
      { feature: 'Go live in the directory', free: false, pro: 'On submit', team: 'On submit' },
      { feature: 'Apply to open board seats', free: false, pro: true, team: true },
      { feature: 'Accept a board invite', free: 'After live', pro: true, team: true },
      { feature: 'Inbox with founders', free: false, pro: true, team: true },
      { feature: 'Send FAST to founders', free: false, pro: true, team: true },
      { feature: 'Take paid sessions (earn 85%)', free: 'After live', pro: true, team: true },
    ],
  },
]

export function resolveUpgradeMembership(args: {
  canAccessAdvisorTeam: boolean
  canAccessPaidAdvisorTier: boolean
}): UpgradeMembership {
  if (args.canAccessAdvisorTeam) {
    return 'team'
  }
  if (args.canAccessPaidAdvisorTier) {
    return 'pro'
  }
  return 'free'
}

export function resolveUpgradePaywallIntent(args: {
  canAccessAdvisorTeam: boolean
  canAccessPaidAdvisorTier: boolean
  isTeamTargeted: boolean
  productId?: string | null
  prompt?: string | null
}): UpgradePaywallIntent {
  if (args.canAccessAdvisorTeam) {
    return 'onTeam'
  }
  if (args.isTeamTargeted || args.productId === ADVISOR_TEAM_UPGRADE_PRODUCT_ID) {
    return 'team'
  }
  if (args.canAccessPaidAdvisorTier) {
    return 'onPro'
  }
  if (
    args.productId === WIL_AI_UPGRADE_PRODUCT_ID ||
    (args.prompt === 'subscribe-lite' && args.productId !== ADVISOR_TEAM_UPGRADE_PRODUCT_ID)
  ) {
    return 'marketplace'
  }
  return 'explore'
}

export function membershipFromPreviewIntent(intent: UpgradePaywallIntent): UpgradeMembership {
  if (intent === 'onTeam') {
    return 'team'
  }
  if (intent === 'onPro') {
    return 'pro'
  }
  return 'free'
}

/**
 * Conversion compare rows for /advisors/upgrade, keyed by productId plus the `free` baseline.
 * `freeWilMessages` is the metered allotment for this environment (`FREE_WIL_MESSAGE_LIMIT`).
 */
export function advisorsUpgradeComparisonRows(freeWilMessages: number) {
  const byPlan = (free: UpgradeCompareValue, pro: UpgradeCompareValue, team: UpgradeCompareValue) => ({
    free,
    [WIL_AI_UPGRADE_PRODUCT_ID]: pro,
    [ADVISOR_TEAM_UPGRADE_PRODUCT_ID]: team,
  })
  return UPGRADE_COMPARE_GROUPS.flatMap(group =>
    group.rows.map(row => {
      const wilRow = row.feature === `Chat with ${WIL_DISPLAY_NAME}`
      return {
        key: `${group.label}:${row.feature}`,
        title: group.label === 'As an Advisor' ? `Advisors: ${row.feature}` : row.feature,
        values: byPlan(wilRow ? `${freeWilMessages} messages` : row.free, row.pro, row.team),
      }
    })
  )
}

/** FAQ for /advisors/upgrade with plan prices taken from the live catalog (e.g. "$49/mo"). */
export function advisorsUpgradeAnswers({ proPrice, teamPrice }: { proPrice: string; teamPrice: string }) {
  return [
    {
      question: 'Do I need Advisor Team to message directory advisors?',
      answer: `No. Startups Pro (${proPrice}) unlocks marketplace messaging, board invites, applications, Inbox, and sending FAST. Advisor Team is only for the Startups.com staff roster — Ed, Ryan, Steve, and Wil.`,
    },
    {
      question: 'Are paid sessions included in a plan?',
      answer:
        'No. Sessions sit on top of the subscription. Any signed-in founder can book a 15, 30, 45, or 60 minute session with a card. The advisor sets the rate; founders pay that listed price. We withhold 20%, 15%, or 10% from the advisor’s payout depending on the advisor’s plan.',
    },
    {
      question: `Is Advisor Team (${teamPrice}) the plan to become an advisor?`,
      answer:
        'No. There is no separate advisor plan. Apply on any plan; you go live when you are on Startups Pro or Advisor Team. Advisor Team is a founder subscription for the Startups.com team, not a listing fee.',
    },
    {
      question: `What is the difference between ${WIL_DISPLAY_NAME} and Wil?`,
      answer: `${WIL_DISPLAY_NAME} is the AI advisor — metered on Free, unlimited on Startups Pro. Wil Schroter is a human on the Advisor Team roster. They are not the same unlock.`,
    },
    {
      question: "If I advise, does a founder's subscription pay me?",
      answer:
        'No. Subscriptions stay with Startups.com. You earn equity through FAST and cash from paid sessions (85% after holdback). Team DMs are staff work, not marketplace payouts.',
    },
    {
      question: 'Can I cancel?',
      answer: 'Yes. Both Startups Pro and Advisor Team are monthly and can be canceled anytime.',
    },
  ]
}
